import { useEffect, useState, type FormEvent } from 'react';
import { ROLES, StaffCreate, jstDate, type Role, type StaffMember } from '@torapon/shared';
import { api, HttpError } from '../lib/api';
import { sendInvite } from '../lib/invite';
import { Dialog, useToast } from '../components/ui';
import { useDirectory } from '../lib/directory';
import { useMe } from '../lib/me';
import { putStaff, setRetireAt, updatePerson } from '../lib/fs';
import { useStepUp } from '../components/StepUp';
import { ROLE } from '../components/Shell';
import { fmtDateTime } from '../lib/format';

const STATUS = { invited: ['招待済み・未ログイン', 'warn'], active: ['在籍', 'ok'], retired: ['退職', ''] } as const;

/** 事務所スタッフ（仕様書「人の追加と退職」：システム管理者だけ）。名前・アドレスは Firestore、役割・状態は D1 と Firestore の両方 */
export function StaffPage() {
  const toast = useToast();
  const me = useMe();
  const dir = useDirectory();
  const { ensureRecent } = useStepUp();
  const [rows, setRows] = useState<StaffMember[] | null>(null);
  const [showRetired, setShowRetired] = useState(false);
  const [dialog, setDialog] = useState<{ kind: 'add' } | { kind: 'role' | 'retire'; s: StaffMember } | null>(null);
  const load = () => api.get<{ staff: StaffMember[] }>('/v1/office/staff').then((r) => setRows(r.staff)).catch(() => setRows([]));
  useEffect(() => { void load(); }, []);
  const fail = (e: unknown) => { toast(e instanceof HttpError ? e.message : '保存できませんでした'); if (e instanceof HttpError && e.code === 'VERSION_CONFLICT') void load(); };
  const reinstate = async (s: StaffMember) => {
    if (!(await ensureRecent('スタッフの再雇用'))) return;
    try { await api.post(`/v1/office/staff/${s.id}/reinstate`, {}, { ifMatch: s.version }); await setRetireAt('staff', s.id, null); toast('有効に戻しました'); void load(); } catch (e) { fail(e); }
  };
  const list = (rows ?? []).filter((s) => showRetired || s.status !== 'retired');
  return (
    <div className="page" style={{ display: 'grid', gap: 12 }}>
      <div className="head" style={{ justifyContent: 'space-between' }}>
        <label style={{ fontSize: 13 }}><input type="checkbox" style={{ width: 'auto' }} checked={showRetired} onChange={(e) => setShowRetired(e.target.checked)} /> 退職者も表示</label>
        <button className="btn pri" onClick={() => setDialog({ kind: 'add' })}>＋ スタッフを追加</button>
      </div>
      <div className="panel">
        <table>
          <thead><tr><th>名前</th><th>アドレス</th><th>役割</th><th>状態</th><th></th></tr></thead>
          <tbody>
            {rows === null ? <tr><td colSpan={5} className="muted">読み込み中…</td></tr>
              : list.map((s) => { const d = dir.staff.get(s.id); return (
                <tr key={s.id} aria-label={`${d?.name ?? ''} の行`}>
                  <td><b>{d?.name ?? '—'}</b>{s.id === me?.id && <small>（自分）</small>}</td><td>{d?.email ?? '—'}</td><td>{ROLE[s.role]}</td>
                  <td><span className={`pill ${STATUS[s.status][1]}`}>{STATUS[s.status][0]}</span>{s.retiredAt && <small>{fmtDateTime(s.retiredAt)} から</small>}</td>
                  <td style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                    {s.status !== 'retired' ? <>
                      <button className="btn sm" onClick={() => setDialog({ kind: 'role', s })}>役割を変える</button>
                      {s.id !== me?.id && <button className="btn sm danger" onClick={() => setDialog({ kind: 'retire', s })}>退職</button>}
                    </> : <button className="btn sm" onClick={() => reinstate(s)}>再雇用</button>}
                  </td>
                </tr>
              ); })}
          </tbody>
        </table>
      </div>
      {dialog?.kind === 'add' && <AddStaff onClose={() => setDialog(null)} onDone={() => { setDialog(null); void load(); }} />}
      {dialog?.kind === 'role' && <RoleDialog s={dialog.s} onClose={() => setDialog(null)} onDone={() => { setDialog(null); void load(); }} onFail={fail} />}
      {dialog?.kind === 'retire' && <RetireDialog kind="staff" id={dialog.s.id} version={dialog.s.version} name={dir.staff.get(dialog.s.id)?.name ?? ''} onClose={() => setDialog(null)} onDone={() => { setDialog(null); void load(); }} />}
    </div>
  );
}

function AddStaff({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const me = useMe();
  const dir = useDirectory();
  const toast = useToast();
  const { ensureRecent } = useStepUp();
  const [f, setF] = useState({ name: '', email: '', role: 'dispatcher' as Role });
  const [err, setErr] = useState('');
  const submit = async (e: FormEvent) => {
    e.preventDefault(); setErr('');
    const r = StaffCreate.safeParse(f);
    if (!r.success) { setErr(r.error.issues[0]?.message ?? '入力を確かめてください'); return; }
    if ([...dir.staff.values()].some((s) => s.email === r.data.email)) { setErr('このメールアドレスはもう登録されています'); return; }
    if (!(await ensureRecent('スタッフの登録'))) return;
    try {
      const { id } = await api.post<{ id: string }>('/v1/office/staff', { role: r.data.role });
      await putStaff(id, { officeId: me!.officeId, name: r.data.name, email: r.data.email, role: r.data.role, active: true, uid: null });
      toast(`${r.data.email} を登録しました。初めてログインすると「在籍」になります`);
      await sendInvite('staff', id, toast);
      onDone();
    } catch (er) { setErr(er instanceof HttpError ? er.message : '登録できませんでした'); }
  };
  return (
    <Dialog title="スタッフを追加" onClose={onClose}>
      <form onSubmit={submit} style={{ display: 'grid', gap: 10 }}>
        <div className="field"><label htmlFor="sn">名前（必須）</label><input id="sn" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></div>
        <div className="field"><label htmlFor="se">Google のアドレス（必須）</label><input id="se" type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></div>
        <div className="field"><label htmlFor="sr">役割</label><select id="sr" value={f.role} onChange={(e) => setF({ ...f, role: e.target.value as Role })}>{ROLES.map((r) => <option key={r} value={r}>{ROLE[r]}</option>)}</select></div>
        {err && <p className="err" role="alert">{err}</p>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}><button type="button" className="btn" onClick={onClose}>やめる</button><button className="btn pri">追加する</button></div>
      </form>
    </Dialog>
  );
}

function RoleDialog({ s, onClose, onDone, onFail }: { s: StaffMember; onClose: () => void; onDone: () => void; onFail: (e: unknown) => void }) {
  const toast = useToast();
  const dir = useDirectory();
  const { ensureRecent } = useStepUp();
  const [role, setRole] = useState<Role>(s.role);
  const [err, setErr] = useState('');
  const save = async () => {
    setErr('');
    if (!(await ensureRecent('役割の変更'))) return;
    try {
      // API で「運行管理者がいなくならないか」を確かめてから、名簿（Firestore）の役割を変える
      await api.patch(`/v1/office/staff/${s.id}`, { role }, { ifMatch: s.version });
      await updatePerson('staff', s.id, { role });
      toast(`${dir.staff.get(s.id)?.name ?? ''}さんを${ROLE[role]}にしました`);
      onDone();
    } catch (e) { if (e instanceof HttpError && e.code === 'LAST_MANAGER') setErr(e.message); else { onFail(e); onClose(); } }
  };
  return (
    <Dialog title={`${dir.staff.get(s.id)?.name ?? ''}さんの役割を変える`} onClose={onClose}>
      <div className="field"><label htmlFor="rr">役割</label><select id="rr" value={role} onChange={(e) => setRole(e.target.value as Role)}>{ROLES.map((r) => <option key={r} value={r}>{ROLE[r]}</option>)}</select></div>
      <p className="muted" style={{ margin: 0, fontSize: 13 }}>運行管理者が 1 人もいなくなる変更はできません。変更は操作ログに残ります。</p>
      {err && <p className="err" role="alert">{err}</p>}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}><button className="btn" onClick={onClose}>やめる</button><button className="btn pri" disabled={role === s.role} onClick={save}>変える</button></div>
    </Dialog>
  );
}

/** 退職の手続き（ドライバー・スタッフ共通）：最終出勤日の翌日からログインできない */
export function RetireDialog({ kind, id, version, name, onClose, onDone }: { kind: 'drivers' | 'staff'; id: string; version: number; name: string; onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  const { ensureRecent } = useStepUp();
  const [lastDay, setLastDay] = useState(jstDate(new Date().toISOString()));
  const [checks, setChecks] = useState({ phone: kind === 'staff', account: false });
  const [err, setErr] = useState('');
  const save = async () => {
    setErr('');
    if (!(await ensureRecent('退職の手続き'))) return;
    try {
      const r = await api.post<{ retireAt: string }>(`/v1/office/${kind}/${id}/retire`, { lastDay }, { ifMatch: version });
      await setRetireAt(kind, id, r.retireAt);
      toast(`${name}さんの退職の手続きをしました（${lastDay} の翌日からログインできません）`);
      onDone();
    } catch (e) { setErr(e instanceof HttpError ? e.message : '手続きできませんでした'); }
  };
  return (
    <Dialog title={`${name}さんの退職の手続き`} onClose={onClose}>
      <div className="field"><label htmlFor="ld">最終出勤日</label><input id="ld" type="date" value={lastDay} onChange={(e) => setLastDay(e.target.value)} /></div>
      {kind === 'drivers' && <p className="muted" style={{ margin: 0, fontSize: 13 }}>終わっていない配送は、先に付け替えか取り消しをしてください。勤務中なら先に勤務を終了します。</p>}
      {kind === 'drivers' && <label><input type="checkbox" style={{ width: 'auto' }} checked={checks.phone} onChange={(e) => setChecks({ ...checks, phone: e.target.checked })} /> 貸与のスマホを回収した</label>}
      <label><input type="checkbox" style={{ width: 'auto' }} checked={checks.account} onChange={(e) => setChecks({ ...checks, account: e.target.checked })} /> 会社のアカウントなら Workspace で止めた（個人の Gmail ならこの手続きで使えなくなる）</label>
      <p className="muted" style={{ margin: 0, fontSize: 13 }}>点呼や運転日報などの記録は、法令の期間そのまま残ります。あとで「再雇用」で戻せます。</p>
      {err && <p className="err" role="alert">{err}</p>}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}><button className="btn" onClick={onClose}>やめる</button><button className="btn danger" disabled={!checks.phone || !checks.account} onClick={save}>退職の手続きをする</button></div>
    </Dialog>
  );
}
